import React from 'react';
import { Alert } from 'react-native';
import * as Picker from 'expo-image-picker';

export type PickedMedia = Picker.ImagePickerResult;
type ImagePickerOptions = Picker.ImagePickerOptions;
type MediaType = 'image' | 'video';
type UseCase = 'profile' | 'post';

const imagePickerConfig: ImagePickerOptions = {
  mediaTypes: Picker.MediaTypeOptions.Images,
  allowsEditing: true,
  aspect: [1, 1],
  quality: 0.2,
  allowsMultipleSelection: false,
  exif: false,
  base64: false,
};

const videoPickerConfig: ImagePickerOptions = {
  mediaTypes: Picker.MediaTypeOptions.Videos,
  quality: 1,
  videoQuality: 1, // medium quality
  videoExportPreset: Picker.VideoExportPreset.Passthrough,
  allowsMultipleSelection: false,
  exif: false,
  base64: false,
};

const getPermissionStatus = async (): Promise<Picker.PermissionStatus> => {
  const { status } = await Picker.requestMediaLibraryPermissionsAsync();
  return status;
};

const useMediaPicker = (mediaType: MediaType, useCase: UseCase = 'post') => {
  const [media, setMedia] = React.useState<PickedMedia>();

  const pickImage = React.useCallback(async (): Promise<PickedMedia> => {
    let config: ImagePickerOptions = imagePickerConfig;
    if (useCase === 'post') {
      config = {
        ...config,
        quality: 1,
        allowsEditing: false,
        aspect: undefined,
      };
    } else if (useCase === 'profile') {
      config = {
        ...config,
        aspect: [1, 1],
      };
    }

    let result = await Picker.launchImageLibraryAsync(config);

    if (!result.cancelled && result) {
      setMedia(result);
    }

    return result;
  }, [useCase]);

  const pickVideo = React.useCallback(async (): Promise<PickedMedia> => {
    let result = await Picker.launchImageLibraryAsync(videoPickerConfig);

    if (!result.cancelled) {
      setMedia(result);
    }

    return result;
  }, [setMedia]);

  React.useEffect(() => {
    (async () => {
      const status = await getPermissionStatus();
      if (status !== Picker.PermissionStatus.GRANTED) {
        Alert.alert('Sorry, we need camera roll permissions to work!');
      }
    })();
  }, []);

  return React.useMemo(
    () => ({
      media,
      pickMedia: mediaType === 'image' ? pickImage : pickVideo,
    }),
    [media, mediaType, pickImage, pickVideo]
  );
};

export default useMediaPicker;
